import { motion } from "framer-motion";
import { FOUNDED_YEAR } from "@/lib/site-data";

const CHAPTERS = [
  {
    n: "I.",
    title: "Small groups, deep access.",
    body: "We keep private and semi-private departures. No coach buses, no rushed itineraries. Your driver, your guide, your pace.",
  },
  {
    n: "II.",
    title: "Local, always.",
    body: "Every guide is Bhutanese, licensed, and quietly extraordinary. Homestays with weavers, tea with monastic scholars, kitchens of grandmothers who cook ema datshi from memory.",
  },
  {
    n: "III.",
    title: "Slow travel, on purpose.",
    body: "Bhutan is measured in Gross National Happiness, not GDP. We honour that pace. Fewer sites, more presence.",
  },
  {
    n: "IV.",
    title: "One quote, everything included.",
    body: "SDF, permits, private vehicle, guide, meals, hotels, festival seats, domestic flights. What you see is what you pay.",
  },
];

const item = {
  hidden: { y: 60, opacity: 0 },
  show: { y: 0, opacity: 1, transition: { duration: 0.9, ease: [0.76, 0, 0.24, 1] } },
};

export default function Manifesto() {
  return (
    <section
      id="manifesto"
      data-testid="manifesto-section"
      className="relative py-24 md:py-40 bg-background"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        {/* section header */}
        <div className="grid md:grid-cols-[220px_1fr] gap-10 md:gap-20 mb-20 md:mb-28">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="text-[11px] uppercase tracking-[0.3em] text-foreground/60 pt-3"
          >
            <div className="flex items-center gap-3">
              <span className="w-6 h-px bg-primary" />
              <span>Ch. 02 · Manifesto</span>
            </div>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
            className="font-serif font-light text-[9vw] md:text-[5.4vw] leading-[0.94] tracking-tighter max-w-4xl"
          >
            We don't sell tours.
            <br />
            We compose <em className="italic text-primary">journeys</em>.
          </motion.h2>
        </div>

        {/* chapters */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={{ show: { transition: { staggerChildren: 0.15 } } }}
          className="grid md:grid-cols-2 gap-x-16 gap-y-20"
        >
          {CHAPTERS.map((c) => (
            <motion.div
              key={c.n}
              variants={item}
              className="relative pl-2 md:pl-0"
            >
              <div className="flex items-baseline gap-6 md:gap-10">
                <span className="font-serif italic text-6xl md:text-8xl font-light text-primary/25 leading-none shrink-0">
                  {c.n}
                </span>
                <div>
                  <h3 className="font-serif text-2xl md:text-4xl font-light leading-tight tracking-tight">
                    {c.title}
                  </h3>
                  <p className="mt-4 text-base md:text-lg font-light text-foreground/75 leading-relaxed max-w-md">
                    {c.body}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* stats footer */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="mt-24 md:mt-32 grid grid-cols-2 md:grid-cols-4 border-t border-foreground/15"
        >
          {[
            ["1,200+", "Travellers curated"],
            [String(new Date().getFullYear() - FOUNDED_YEAR), "Years in Paro"],
            ["24hr", "Response promise"],
            ["100%", "Local guides"],
          ].map(([n, l], i) => (
            <div
              key={l}
              className={`py-8 md:py-10 pr-4 ${
                i > 0 ? "md:border-l border-foreground/15" : ""
              } ${i > 0 && i % 2 !== 0 ? "border-l border-foreground/15 md:border-l" : ""} ${
                i >= 2 ? "border-t md:border-t-0 border-foreground/15" : ""
              }`}
            >
              <div className="font-serif text-4xl md:text-6xl font-light leading-none">
                {n}
              </div>
              <div className="mt-3 text-[10px] uppercase tracking-[0.24em] text-foreground/60">
                {l}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
